import React from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import { observer } from "mobx-react-lite";
import classNames from "classnames";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import type { ViewComponent } from "@frui.ts/views";
import { View, registerView } from "@frui.ts/views";
import DeadLetterTopicsSearchDetailViewModel from "viewModels/administration/deadLetterTopics/search/deadLetterTopicsSearchDetailViewModel";
import PrevNextNavigationHeader from "views/common/prevNextNavigationHeader";
import Section from "views/common/section";
import { formatDateTime } from "utils/localizations";

export const DeadLetterTopicsSearchDetailView: ViewComponent<DeadLetterTopicsSearchDetailViewModel> = observer(({ vm }) => {
  // We need to fake disabled state, because Bootstrap doesn't propagate events on disabled buttons
  const ResubmissionButton = ({ disabled }: { disabled?: boolean }) => {
    const refundOnClick = disabled ? () => null : vm.onResubmissionClick;

    return (
      <Button
        variant="primary"
        disabled={vm.busyWatcher.isBusy}
        className={classNames("text-uppercase", { disabled })}
        onClick={refundOnClick}>
        {vm.translate("resubmission")}
      </Button>
    );
  };
  const DiscardButton = ({ disabled }: { disabled?: boolean }) => {
    const rejectOnClick = disabled ? () => null : vm.onDiscardClick;

    return (
      <Button
        variant="danger"
        disabled={vm.busyWatcher.isBusy}
        className={classNames("text-uppercase", { disabled })}
        onClick={rejectOnClick}>
        {vm.translate("discard")}
      </Button>
    );
  };

  return (
    <Modal scrollable show={true} centered={true} onHide={vm.requestClose} dialogClassName="modal-90w">
      <Modal.Header closeButton={true}>
        <PrevNextNavigationHeader vm={vm} />
        {vm.allowWrite && (
          <ButtonToolbar className="flex-grow-1 justify-content-end">
            <div className="kit__button">
              <ResubmissionButton />
            </div>
            <div className="kit__button">
              <DiscardButton />
            </div>
          </ButtonToolbar>
        )}
      </Modal.Header>
      <Modal.Body>
        {vm.item ? (
          <LoaderOverlay loading={vm.busyWatcher.isBusy}>
            <View vm={vm.alertsViewModel} useLifecycle={true} />
            <Row className="mb-4">
              <Col>
                <Section title={vm.translate("name")} context={vm.item}>
                  <Form.Row>
                    <Form.Group as={Col} sm={6} md={4} lg={3} xl={4}>
                      <Form.Label>{vm.translateSubAttribute("deadLetterTopicsDetail", "originalProducedAt")}</Form.Label>
                      <FakeInput target={vm.item} property="originalProducedAt" valueFormatter={formatDateTime} />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4} lg={3} xl={4}>
                      <Form.Label>{vm.translateSubAttribute("deadLetterTopicsDetail", "messageType")}</Form.Label>
                      <FakeInput target={vm.item} property="messageType" valueFormatter={value => vm.translateTypeState(value)} />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4} lg={3} xl={2}>
                      <Form.Label>{vm.translateSubAttribute("deadLetterTopicsDetail", "topic")}</Form.Label>
                      <FakeInput target={vm.item} property="topic" valueFormatter={value => vm.translateTopicState(value)} />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4} lg={3} xl={2}>
                      <Form.Label>{vm.translateSubAttribute("deadLetterTopicsDetail", "deliveryAttempts")}</Form.Label>
                      <FakeInput target={vm.item} property="deliveryAttempts" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4} lg={3} xl={4}>
                      <Form.Label>{vm.translateSubAttribute("deadLetterTopicsDetail", "messageRequestId")}</Form.Label>
                      <FakeInput target={vm.item} property="messageRequestId" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4} lg={3} xl={4}>
                      <Form.Label>{vm.translateSubAttribute("deadLetterTopicsDetail", "chargeOrderId")}</Form.Label>
                      <FakeInput target={vm.item} property="chargeOrderId" />
                    </Form.Group>
                    <Form.Group as={Col} sm={6} md={4} lg={3} xl={4}>
                      <Form.Label>{vm.translateSubAttribute("deadLetterTopicsDetail", "chargeId")}</Form.Label>
                      <FakeInput target={vm.item} property="chargeId" />
                    </Form.Group>
                  </Form.Row>
                </Section>
              </Col>
            </Row>
          </LoaderOverlay>
        ) : (
          <LoaderOverlay loading={true} style={{ minHeight: 200 }} />
        )}
      </Modal.Body>
    </Modal>
  );
});

registerView(DeadLetterTopicsSearchDetailView, DeadLetterTopicsSearchDetailViewModel);
